import React from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Message,
  Modal,
  Text,
  toast,
  useAppTheme,
} from '@workwhile/ui'
import { useAssessmentDetailContextValue } from '../context/AssessmentDetailProvider'
import type { EvaluationStatus } from '../schema'

type ConfirmEvaluationModalProps = {
  open: boolean
  questionEvaluations: Record<string, EvaluationStatus>
  justifications: Record<string, string | undefined>
  onClose: () => void
  onSuccess: () => void
}

const STATUS_LABEL: Record<EvaluationStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
}

const StatusText = ({ status }: { status?: EvaluationStatus }) => {
  const { colors } = useAppTheme()
  return (
    <Text
      color={
        status === 'pass'
          ? colors.success
          : status === 'fail'
            ? colors.error
            : colors.lightText
      }
    >
      {status ? STATUS_LABEL[status] : '—'}
    </Text>
  )
}

export const ConfirmEvaluationModal = ({
  open,
  questionEvaluations,
  justifications,
  onClose,
  onSuccess,
}: ConfirmEvaluationModalProps) => {
  const { colors } = useAppTheme()
  const {
    state: {
      workerName,
      workerId,
      positionName,
      positionId,
      requirements,
      submitEvaluationsState: { isPending, isError, errorMessage },
    },
    actions: { submitEvaluations, reset },
  } = useAssessmentDetailContextValue()

  const handleClose = () => {
    if (isPending) return
    reset()
    onClose()
  }

  const handleSave = () => {
    const questionItems = requirements.flatMap((requirement) =>
      requirement.questions.map((question) => {
        const justification = justifications[String(question.id)]?.trim()
        return {
          questionMappingId: question.id,
          evaluation: questionEvaluations[String(question.id)] === 'pass',
          justification: justification ? justification : null,
        }
      })
    )

    submitEvaluations(
      {
        positionId,
        userId: workerId,
        evaluations: questionItems,
      },
      {
        onSuccess: () => {
          toast.success(
            "Evaluation saved! Submit another one if you'd like to change it.",
            { position: 'top-center' }
          )
          onSuccess()
        },
      }
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      loading={isPending}
      position="top"
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isPending} disabled={isPending}>
            Save
          </Button>
        </Flex>
      }
    >
      <Heading level={2} m={0}>
        Confirm evaluation
      </Heading>

      <Box maxHeight="70vh" overflowY="auto" mt={3}>
        <Flex flexDirection="column" gap={3}>
          <Box>
            <Text fontWeight="bold">Worker</Text>
            <Text>
              {workerName} ({workerId})
            </Text>
          </Box>
          <Box>
            <Text fontWeight="bold">Position</Text>
            <Text>
              {positionName} ({positionId})
            </Text>
          </Box>

          <Box>
            <Text fontWeight="bold" mb={2}>
              Requirements
            </Text>
            {requirements.map((requirement, index) => (
              <Flex
                key={requirement.id}
                backgroundColor={
                  index % 2 === 0 ? colors.offWhite : 'transparent'
                }
                borderRadius="standard"
                flexDirection="column"
                p={2}
                gap={2}
              >
                <Text variant="bodyMedium">{requirement.name}</Text>

                {requirement.questions.map((question) => {
                  const status = questionEvaluations[String(question.id)]
                  const justification =
                    justifications[String(question.id)]?.trim() ?? ''
                  return (
                    <Flex
                      key={question.id}
                      flexDirection="column"
                      gap={1}
                      pl={3}
                    >
                      <Flex
                        flexDirection="row"
                        gap={3}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Text fontSize={1}>{question.name}</Text>
                        <StatusText status={status} />
                      </Flex>
                      {justification ? (
                        <Text
                          color="lightText"
                          fontSize={1}
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {justification}
                        </Text>
                      ) : null}
                    </Flex>
                  )
                })}
              </Flex>
            ))}
          </Box>

          {isError && errorMessage ? (
            <Message variant="error" borderRadius="small">
              {errorMessage}
            </Message>
          ) : null}
        </Flex>
      </Box>
    </Modal>
  )
}
